import {
  all,
  filter,
  find,
  groupBy,
  isEmpty,
  keys,
  map,
  prop,
  propEq,
  sortBy,
  values,
} from 'ramda';
import createCachedSelector from 're-reselect';
import * as R from 'remeda';
import { createSelector } from 'reselect';
import { IChecklistFilter, IChecklistGroup } from '@bridebook/web/lib/checklist/types';
import { getGroupsTranslation } from '@bridebook/web/lib/countries/data/groups';
import { getTasksTranslation } from '@bridebook/web/lib/countries/data/tasks';
import type { IApplicationState, TTaskSerialized } from '@bridebook/web/lib/types';
import { createDeepEqualSelector } from '@bridebook/web/lib/utils/selectors';
import { sortPeriods } from '../Tasks.utils';

/* ############################################################################
 *  INTERNAL input-selectors without mutations
 * ######################################################################### */

export const getChecklistInitializedAt = (state: IApplicationState) =>
  state.weddings.profile.tasks.initializedAt;
const seedGroups = (state: IApplicationState) => state.app.groups;
const userLocale = (state: IApplicationState) => state.users.user?.l10n?.locale;
const groups = (state: IApplicationState) => state.checklist.groups;
const periods = (state: IApplicationState) => state.checklist.periods;
const tasks = (state: IApplicationState) => state.checklist.tasks;
const tasksInitial = (state: IApplicationState) => state.checklist.tasksInitial;
export const getDetailsTaskId = (state: IApplicationState) => state.checklist.detailsTaskId;

/* ############################################################################
 *  MEMOIZED also with multiple cache and deep equal memoization
 * ######################################################################### */

/**
 * Check if checklist groups have been loaded and are present in the state
 */
export const areGroupsLoaded = createSelector(groups, (groups) => keys(groups).length > 0);
/**
 * Get an object with filter names as keys, containing arrays of period names
 */
export const getPeriodIds = createDeepEqualSelector(
  createSelector(periods, (periods) =>
    map<typeof periods, Record<IChecklistFilter, Array<string>>>(
      (periods) => sortPeriods(keys(periods)),
      periods,
    ),
  ),
  (periodIds) => periodIds,
);

/**
 * Returns an array of task IDs for a given period and filter
 */
const _getGroupTasks = createCachedSelector(
  (_: IApplicationState, period: string) => period,
  (_: IApplicationState, _period: string, filter: IChecklistFilter) => filter,
  periods,
  (period, filter, periods): Array<string> => periods[filter][period],
)((_: IApplicationState, period, filter) => `${period}:${filter}`);

// Deep equal compare before update
export const getGroupTasks = createCachedSelector(_getGroupTasks, (groupTasks) => groupTasks)(
  (_: IApplicationState, period, filter) => `${period}:${filter}`,
  {
    selectorCreator: createDeepEqualSelector,
  },
);

// Returns translated name of the task group, cached for performance
export const _getTaskGroupName = createCachedSelector(
  seedGroups,
  userLocale,
  (_: IApplicationState, taskId: string) => taskId,
  (seedGroups, _userLocale, taskId) => {
    const seedTask = seedGroups[taskId];
    if (seedTask) {
      const key = seedTask.i18nKey as string;
      return getGroupsTranslation[key]?.().name || seedTask.name || '';
    }
    return undefined;
  },
)((_, taskId) => taskId);

export const _getTaskGroup = createCachedSelector(
  groups,
  seedGroups,
  _getTaskGroupName,
  (_: IApplicationState, taskId: string) => taskId,
  (groups, seedGroups, taskGroupName, taskId) => {
    const seedTask = seedGroups[taskId];
    const taskGroup: IChecklistGroup = { ...groups[taskId] };
    if (seedTask && taskGroup) {
      taskGroup.name = taskGroupName || seedTask.name || '';
    }
    return taskGroup;
  },
)((_, taskId) => taskId);

// Deep equal compare before update
export const getTaskGroup = createCachedSelector(_getTaskGroup, (taskGroup) => taskGroup)(
  (_, taskId) => taskId,
  {
    selectorCreator: createDeepEqualSelector,
  },
);

export const getTaskName = createCachedSelector(
  tasksInitial,
  userLocale,
  (_: IApplicationState, task: TTaskSerialized) => task,
  (tasksInitial, _userLocale, task) => {
    const getInitialTaskName = () => {
      const defaultName = tasksInitial[task.id].name;
      const key = tasksInitial[task.id].i18nKey;
      return getTasksTranslation()[key] || defaultName;
    };
    return tasksInitial[task.id] ? getInitialTaskName() : task.name;
  },
)((_, task) => task.id);

export const getTaskAssignee = createCachedSelector(
  tasks,
  (_: IApplicationState, taskId: string) => taskId,
  (tasks, taskId) => {
    // @ts-ignore FIXME
    const group = find(propEq('group', taskId))(values(tasks));
    // @ts-ignore FIXME
    return group ? group.assignee : undefined;
  },
)((_, taskId) => taskId);

export const getShowDetailsTask = createSelector(
  [seedGroups, getDetailsTaskId],
  (list, detailsTaskId) => (detailsTaskId ? list[detailsTaskId] : undefined),
);

/**
 * Count groups where all tasks done.
 */
export const getTasksDoneCount = createSelector([tasks], (tasks) => {
  const tasksCompleted = R.pipe(
    values(tasks) as Required<TTaskSerialized>[],
    R.groupBy(R.prop('group')),
    R.toPairs,
    R.map((i) => (all(propEq('done', true), i[1]) ? true : null)),
  ).filter(Boolean).length;
  return tasksCompleted;
});

export const getTaskList = createSelector([tasks], (taskList) => {
  const tasksSorted = sortBy(prop('order'))(values(taskList));
  return groupBy(prop('group'), tasksSorted as Required<TTaskSerialized>[]);
});

export const isGroupCompleted = createSelector(
  [(state: IApplicationState) => state.checklist.filter],
  (filter) => filter === 'completed',
);

/**
 * Whether every task of the group open in the task drawer is done. False with no drawer
 * open: a tick from elsewhere in the app, such as the planner chat, must not read as a
 * completed group and close a drawer that is not there.
 */
export const getDoneGroup = createSelector([tasks, getDetailsTaskId], (taskList, groupId) => {
  if (!groupId) return false;
  const groupTasks = values(taskList).filter((task) => task.group === groupId);
  return groupTasks.length > 0 && all(propEq('done', true), groupTasks);
});

export const getDrawerGroupPeriod = createSelector(
  [tasks, getDetailsTaskId],
  (taskList, groupId): string => {
    if (isEmpty(taskList)) return '';

    const task = find((task: TTaskSerialized) => task.group === groupId)(values(taskList));

    return task?.period ?? '';
  },
);

export const getEarliestPeriod = createSelector([tasks], (taskList) =>
  !taskList || isEmpty(taskList) ? '' : sortBy(prop('order'))(values(taskList))[0].period,
);

export const getNextTaskGroup = createSelector([tasks, seedGroups], (taskList, taskGroups) => {
  const nextTask = filter(
    (item) => !item.done && !item.custom,
    sortBy(prop('order'))(values(taskList)),
  )[0];

  const nextTaskGroupId = nextTask ? nextTask.group : undefined;
  return nextTaskGroupId ? taskGroups[nextTaskGroupId] : undefined;
});

export const getNumberOfNextTasks = createCachedSelector(
  tasks,
  seedGroups,
  (_: IApplicationState, numberOfTasks: number) => numberOfTasks,
  (taskList, taskGroups, numberOfTasks) => {
    const tasksList = values(taskList);
    const sortedTasks = sortBy(prop('order'))(tasksList);
    const undoneTasks = sortedTasks.filter((task) => !task.done);

    const nextTasks = undoneTasks
      .filter((task, index, self) => index === self.findIndex((t) => t.group === task.group))
      .slice(0, numberOfTasks);

    return nextTasks.map((item) => (item.custom || !item.group ? item : taskGroups[item.group]));
  },
)((_, numberOfTasks) => numberOfTasks);

export const getGroupSupplierSlug = createSelector(
  [seedGroups, getDetailsTaskId],
  (groups, groupId) =>
    isEmpty(groups) || !groups || !groupId || !groups[groupId]
      ? undefined
      : groups[groupId].supplierType,
);

export const isDetailsDrawerOpened = createSelector(
  [groups, getDetailsTaskId],
  (groups, detailsTaskId) => Boolean(detailsTaskId && groups[detailsTaskId]),
);

export const getIsChecklistInitialised = createSelector(
  [(state) => state.checklist.initialised],
  (initialised) => initialised,
);

export const getIsChecklistLoaded = createSelector(
  [(state) => state.checklist.loaded],
  (loaded) => loaded,
);

export const getIsChecklistInitialising = createSelector(
  [(state) => state.checklist.reInitialising],
  (reInitialising) => reInitialising,
);

export const getShowAddTask = createSelector(
  [(state) => state.checklist.showAddTask],
  (showAddTask) => showAddTask,
);
